import React, { useEffect, useState } from 'react';
import { EticketEmitProps } from '@msc-b2c/interfaces/types';
import { Button, Icon, InputText, Text } from '@msc-b2c/shared-components';
import {
  closeLoader,
  ETicketMyMsc,
  getActiveSession,
  getWciOpensOn,
  openLoader,
  replacePlaceholders,
  useGetBookingQuery,
  validateFormField,
} from '@msc-b2c/shared-utils';
import { FieldValues, useForm } from 'react-hook-form';
import './eticketemit.module.scss';
import { isB2CBooking } from '../utils/myAreaUtils';
import { skipToken } from '@reduxjs/toolkit/query';
import { LoginMode } from '@msc-b2c/interfaces/enums';

export const EticketEmit = ({
  id,
  emailInputLabel,
  emailInputPlaceholder,
  emailInputValidationRules,
  b2cConfig,
  b2bConfig,
}: Readonly<EticketEmitProps>) => {
  const {
    register,
    trigger,
    formState: { errors },
    clearErrors,
    handleSubmit,
    setValue,
    watch,
  } = useForm<{ email: string }>({ shouldFocusError: false });

  const emailValue = watch('email');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    isLoading ? openLoader('.loader') : closeLoader('.loader');
  }, [isLoading]);

  useEffect(() => {
    setValue('email', loginMode === LoginMode.FULL ? email : '');
  }, []);

  const { email, loginMode, bookingNumber } = getActiveSession();
  const { data: booking } = useGetBookingQuery(
    bookingNumber && bookingNumber !== '' ? bookingNumber : skipToken
  );

  if (!booking || !bookingNumber) return null;

  const b2CBooking = isB2CBooking(booking);
  const isEligibleForETickets =
    booking?.isEligibleForETickets && (b2CBooking || booking?.agency?.sendDocumentsToPassenger);

  const isB2bSendDocumentsForbidden = !b2CBooking && !booking?.agency?.sendDocumentsToPassenger;
  const contentToDisplay = isB2bSendDocumentsForbidden ? b2bConfig : b2cConfig;

  const onSubmitForm = (data: FieldValues) => {
    setIsLoading(true);
    const eticketEmit = new ETicketMyMsc(false, false, { email: data.email }, { bookingNumber });
    eticketEmit.sendData().finally(() => setIsLoading(false));
  };

  return (
    <div
      id={id}
      className="cmp-eticketemit__container"
    >
      <Icon
        icon={contentToDisplay.eticketIcon}
        styleOption="cmp-eticketemit__icon"
      />
      <Text
        text={contentToDisplay.eticketTitle}
        styleOption="header-m cmp-eticketemit__title"
      />
      <Text
        text={replacePlaceholders(contentToDisplay.eticketDescription, {
          checkInDays: String(getWciOpensOn() ?? ''),
        })}
        styleOption="body-default cmp-eticketemit__description"
      />
      {!isB2bSendDocumentsForbidden && (
        <form
          className="cmp-form cmp-eticketemit__form"
          onSubmit={handleSubmit(onSubmitForm)}
        >
          <InputText
            styleOptions="cmp-eticketemit__input"
            index={1}
            name="email"
            label={emailInputLabel}
            placeholder={emailInputPlaceholder}
            register={register}
            errors={errors}
            disabled={!isEligibleForETickets}
            clearErrors={clearErrors}
            validation={{
              validate: (value) => validateFormField(value, emailInputValidationRules || []),
              onBlur: () => trigger('email'),
            }}
          />
          <Button
            styleOption="cmp-button__primary -extended cmp-eticketemit__button -full-width"
            buttonType="submit"
            disabled={!isEligibleForETickets || !emailValue?.trim()}
            text={b2cConfig.eticketSubmitButtonLabel}
          />
        </form>
      )}
    </div>
  );
};
